import { ChevronsRight } from "lucide-react";
import { useMemo, useState } from "react";
import { NamePicker } from "@/components/NamePicker";
import { useApp } from "@/lib/AppContext";
import { ApiError, type UnnamedSpeaker } from "@/lib/api";
import { personColour, personInitials } from "@/lib/personAvatar";
import { useAudioFragment } from "@/lib/useAudioFragment";
import { FragmentPlayer } from "./FragmentPlayer";

/**
 * UI-16's "Who is this?" card — SPCH-6 asked from outside a recording.
 *
 * It is the one card whose answer is not a button but a *person*, which is why it owns its
 * footer instead of using the shared one: there is no "yes", only "that one" and "someone
 * else".
 *
 * **The candidates are the gallery's, not this screen's.** SPCH-5 records one proposal per
 * voice — who matched and how well — and that is what is offered here, with the same score
 * the recording's own Speakers tab shows. Ranking a second and third name would mean
 * inventing similarities nothing measured, so anyone the gallery did not propose is reached
 * through "Someone else", which is the same typeahead over the same people the Speakers tab
 * uses, and which creates a person for a voice nobody has met before.
 *
 * Confirming is the ordinary SPCH-6 write, so it does what it does everywhere: this
 * recording's candidate samples become gallery entries, and the next recording recognises
 * the voice by itself.
 */
export function SpeakerCard({
  speaker,
  onNamed,
  onSkip,
}: {
  speaker: UnnamedSpeaker;
  onNamed: () => void;
  onSkip: () => void;
}) {
  const { api, entities } = useApp();

  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const persons = useMemo(() => entities.filter((entity) => entity.type === "person"), [entities]);

  // SEC-4's link, minted for this recording the moment the card is on screen. One card is
  // one recording, so the hook's "one link per visit" is exactly one link per question.
  const player = useAudioFragment(api, speaker.sourceMaterialId);

  function name(who: { personId: string } | { name: string }) {
    if (busy) return;
    setBusy(true);
    setProblem(null);

    void api
      .confirmSpeaker(speaker.sourceMaterialId, speaker.id, who)
      .then(() => {
        player.stop();
        onNamed();
      })
      .catch((cause: unknown) => {
        setProblem(
          cause instanceof ApiError && cause.status === 404
            ? "That voice has already been named somewhere else."
            : "That could not be saved. Try again.",
        );
        setBusy(false);
      });
  }

  return (
    <div className="flex flex-col gap-[var(--space-4)]">
      {speaker.sampleText && (
        <p className="text-[19px] leading-[1.7] text-[var(--text-primary)] [text-wrap:pretty]">
          “{speaker.sampleText}”
        </p>
      )}

      {speaker.sampleStartMs !== null && (
        <FragmentPlayer
          startMs={speaker.sampleStartMs}
          endMs={speaker.sampleEndMs}
          playing={player.playing === speaker.id}
          progress={player.playing === speaker.id ? player.progress : 0}
          disabled={player.problem !== null}
          onToggle={() =>
            player.play({ id: speaker.id, startMs: speaker.sampleStartMs!, endMs: speaker.sampleEndMs })
          }
        />
      )}

      {player.problem && (
        <p role="alert" className="text-[length:var(--text-xs)] text-[var(--warning)]">
          {player.problem}
        </p>
      )}

      <p className="text-[length:var(--text-xs)] text-[var(--text-muted)] [text-wrap:pretty]">
        {speaker.matchedPersonId
          ? "One voice in this recording is close to somebody Factorium already knows, but not close enough to be sure."
          : "One voice in this recording does not match anyone Factorium knows well enough."}
      </p>

      {picking ? (
        <NamePicker
          heading="Who is this?"
          persons={persons}
          busy={busy}
          onPick={name}
          onCancel={() => setPicking(false)}
        />
      ) : (
        speaker.matchedPersonId &&
        speaker.matchedPersonName && (
          <button
            type="button"
            disabled={busy}
            onClick={() => name({ personId: speaker.matchedPersonId! })}
            className="flex items-center gap-3 rounded-[var(--radius-lg)] border border-[var(--border-subtle)] px-3 py-2.5 text-left transition hover:border-[var(--border-strong)] hover:bg-[var(--surface-2)] disabled:opacity-50"
          >
            <span
              aria-hidden
              className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[var(--radius-full)] text-[length:var(--text-2xs)] font-bold text-[var(--text-inverse)]"
              style={{ background: personColour(speaker.matchedPersonId) }}
            >
              {personInitials(speaker.matchedPersonName)}
            </span>
            <span className="flex min-w-0 flex-1 flex-col gap-px">
              <span className="truncate font-semibold text-[var(--text-primary)]">{speaker.matchedPersonName}</span>
              <span className="text-[length:var(--text-2xs)] text-[var(--text-muted)]">
                Heard before in your recordings
              </span>
            </span>
            {speaker.matchScore !== null && (
              <span className="shrink-0 font-mono text-[length:var(--text-2xs)] text-[var(--text-muted)]">
                {speaker.matchScore.toFixed(2)} match
              </span>
            )}
          </button>
        )
      )}

      {problem && (
        <p role="alert" className="text-[length:var(--text-xs)] text-[var(--warning)]">
          {problem}
        </p>
      )}

      <div className="flex items-center gap-2 border-t border-[var(--border-subtle)] pt-[var(--space-2)]">
        <button
          type="button"
          onClick={onSkip}
          className="inline-flex items-center gap-1.5 text-[length:var(--text-xs)] text-[var(--text-muted)] transition hover:text-[var(--text-secondary)]"
        >
          <ChevronsRight size={12} strokeWidth={2} />
          Skip for now
        </button>

        <span className="flex-1" />

        {!picking && (
          <button
            type="button"
            disabled={busy}
            onClick={() => setPicking(true)}
            className="rounded-[var(--radius-md)] px-3 py-1.5 text-[length:var(--text-sm)] font-semibold text-[var(--text-secondary)] transition hover:text-[var(--text-primary)] disabled:opacity-50"
          >
            {speaker.matchedPersonId ? "Someone else" : "Say who it is"}
          </button>
        )}
      </div>
    </div>
  );
}
